// Release Theme Registry
//
// Single source of truth for which release identifiers have a defined theme,
// the default/fallback release, and the color-substitution rule used to derive
// the 2610 palette from the 2510 palette.
//
// The two variants of 2610 are derived differently:
//
//   light — pure navy->purple substitution of 2510 light (`derive2610Variant`).
//   dark  — that same substitution, then the accent/surface roles are replaced
//           with the chat widget's dark ramp (`derive2610DarkVariant`).
//
// The dark variant diverges because the mechanical substitution left 2510's
// orange accents in place while turning the surfaces purple, pairing two
// high-saturation hues ~125 degrees apart. The worst case was an #FF8900 logo
// on a #9747FF card at 1.89:1. 2610 dark now takes its palette from
// `components/shared/chat-widget/styles.css`, so the page and the embedded
// widget share one ramp. Light mode keeps purple as the release's identity.

// Identifiers that have a defined Release_Theme.
export const DEFINED_RELEASE_THEMES = ['2510', '2610'];

// Fallback used for unknown/missing identifiers (Requirements 1.5, 4.7).
export const DEFAULT_RELEASE_THEME = '2510';

// Brand color substitution that derives 2610 from 2510 (Requirement 3).
export const BRAND_NAVY = '#001C30';
export const BRAND_PURPLE = '#9747FF';

/**
 * Resolve a requested release identifier to a defined theme id.
 * Returns { releaseId, isFallback } where isFallback indicates that no
 * release-specific theme exists for the requested identifier.
 */
export function resolveReleaseTheme(requestedId) {
  if (DEFINED_RELEASE_THEMES.includes(requestedId)) {
    return { releaseId: requestedId, isFallback: false };
  }
  return { releaseId: DEFAULT_RELEASE_THEME, isFallback: true };
}

/**
 * Pure transform: given a 2510 variant palette (map of cssVar -> value),
 * produce the corresponding 2610 variant palette.
 * - Every variable whose value is exactly BRAND_NAVY becomes BRAND_PURPLE.
 * - Every other variable is retained unchanged.
 * - The --brand-purple variable is added with value BRAND_PURPLE.
 */
export function derive2610Variant(variant2510) {
  const out = {};
  for (const [name, value] of Object.entries(variant2510)) {
    out[name] = value === BRAND_NAVY ? BRAND_PURPLE : value;
  }
  out['--brand-purple'] = BRAND_PURPLE;
  return out;
}

// The chat widget's dark ramp, copied from the `[data-cbw-theme='dark']` block
// in `components/shared/chat-widget/styles.css`. Named after the widget's own
// tokens so the two files can be diffed by eye.
export const CHAT_WIDGET_DARK = {
  primary: '#00DBFF',
  primaryHover: '#2EE4FF',
  primaryContrast: '#001C30',
  bg: '#001C30',
  surface: '#002236',
  surfaceAlt: '#002B45',
  surfaceSunken: '#001525',
  text: '#E4F6FF',
  textMuted: '#8FB3C7',
  border: '#0B3A57',
  borderStrong: '#12506F',
};

// The widget ships four surface steps; the app palette needs eight rungs
// (body through --button-hover-3). These three extend the same hue past the
// widget's range so the whole ramp ascends in lightness monotonically.
const SURFACE_MODAL = '#00263C';
const SURFACE_RAISED = '#003554';
const SURFACE_HOVER_STRONG = '#1A6288';

// Tertiary text. The widget has only two text steps; this third one sits below
// `textMuted` while still clearing 4.5:1 on every surface in the ramp, which
// the 2510 equivalent (#888888, as low as 2.92:1) does not.
const TEXT_TERTIARY = '#7FA3B8';

// Value-level overrides applied on top of `derive2610Variant` for the dark
// variant. Keys are a subset of the 2510 dark variable names, so the derived
// key set is unchanged: every role 2510 defines, 2610 defines too.
//
// `--brand-navy` reverts to navy rather than staying purple: in dark mode the
// surfaces are navy-based, and this token is what `modal.css` reads for the
// dark modal background. `--brand-purple` is still emitted (unchanged) because
// it carries the release's identity in light mode.
//
// `--brand-orange` also keeps its value. It is no longer consumed anywhere in
// 2610 dark, but it names a brand color rather than a role, so redefining it as
// cyan would be a lie; the rules that used to read it are scoped away instead.
export const RELEASE_2610_DARK_OVERRIDES = {
  '--brand-navy': CHAT_WIDGET_DARK.bg,

  '--body-background': CHAT_WIDGET_DARK.surfaceSunken,

  '--bg-white': CHAT_WIDGET_DARK.surface,
  '--bg-light-gray': CHAT_WIDGET_DARK.surfaceAlt,
  '--bg-lighter-gray': SURFACE_RAISED,
  '--bg-button-light': CHAT_WIDGET_DARK.border,
  '--bg-modal-light': SURFACE_MODAL,

  '--text-dark': CHAT_WIDGET_DARK.text,
  '--text-medium': CHAT_WIDGET_DARK.textMuted,
  '--text-light': TEXT_TERTIARY,

  '--title-color': CHAT_WIDGET_DARK.primary,
  '--title-light-color': CHAT_WIDGET_DARK.primaryHover,
  '--banner-text-color': CHAT_WIDGET_DARK.primary,

  '--border-gray': CHAT_WIDGET_DARK.border,
  '--button-hover-1': SURFACE_RAISED,
  '--button-hover-2': CHAT_WIDGET_DARK.borderStrong,
  '--button-hover-3': SURFACE_HOVER_STRONG,

  // The DTA tokens are inert in 2610 (this release renders the new widget, not
  // the `dta-chat` element), but they are recolored anyway so the palette holds
  // no orange/purple leftovers. --gradient-brand resolves through --dta-color1
  // and --dta-color2, so it becomes cyan -> navy without editing the string.
  '--dta-c-background': CHAT_WIDGET_DARK.surface,
  '--dta-c-text': CHAT_WIDGET_DARK.text,
  '--dta-c-primary': CHAT_WIDGET_DARK.primary,
  '--dta-chat-input-background-color': CHAT_WIDGET_DARK.surfaceAlt,
  '--dta-chat-input-border-color': CHAT_WIDGET_DARK.border,
  '--dta-chat-input-btn-bg-color': SURFACE_RAISED,
  '--dta-chat-input-btn-bg-hv-color': CHAT_WIDGET_DARK.borderStrong,
  '--dta-c-neutral-25': CHAT_WIDGET_DARK.surfaceAlt,
  '--dta-c-text--dark': CHAT_WIDGET_DARK.text,
  '--dta-color1': CHAT_WIDGET_DARK.primary,
  '--dta-color2': CHAT_WIDGET_DARK.bg,
};

// Item surfaces for 2610 dark.
//
// The root palette above is a single navy hue, which left the five panels with
// nothing to sit on: `--card-bg` and friends resolved to the same token as the
// band behind them (`--bg-white`), so cards, docs and central links rendered at
// 1.00:1 against their own section — invisible. The drop shadow the shared
// components rely on for separation is a 15% black blur, which does nothing on
// a dark surface.
//
// Fixing that with another navy step alone is not possible: adjacent dark
// surfaces top out around 1.2:1 in luminance, well under the 3:1 WCAG 1.4.11
// asks for a component boundary. So the items take a second hue and an explicit
// ring:
//
//   band   #002236 / #002B45   navy, from the root palette
//   item   #382868             violet, 52-53 deg off the band, lighter than both
//   ring   #7A68B0             3.46:1 vs #002236, 3.10:1 vs #002B45
//   hover  #7526E4             the chat widget's --cbw-primary-active
//
// Violet is 67 deg from the cyan accent, so the two harmonize; the pairing this
// replaced was orange and purple at 126 deg. It also puts 2610's purple back
// into dark mode, which the cyan-only palette had dropped.
//
// These are consumed as literal values in the five 2610 panel CSS files (the
// same way 2510's panels carry #001C30 and #FF8900 literally). The panel blocks
// are asserted against this object; see theme/__tests__/itemSurfaces.test.js.
export const RELEASE_2610_DARK_ITEM_SURFACE = {
  // Resting fill, and the text/glyph colors that sit on it.
  fill: '#382868',
  text: '#E4F6FF', // 11.32:1 on fill
  glyph: '#00DBFF', // cyan accent, 7.52:1 on fill
  // The opt-in ring from the shared components.
  ringWidth: '1px',
  ringColor: '#7A68B0',
  // Hover fill, and the text/glyph colors that sit on it.
  hoverFill: '#7526E4',
  hoverText: '#FFFFFF', // 6.66:1 on hoverFill
  // Deep navy chip behind the content-box icon, so the cyan glyph has something
  // dark to sit on while the card itself is violet.
  chipFill: '#001C30',
  chipGlyph: '#00DBFF', // 10.40:1 on chipFill
};

/**
 * Pure transform: given the 2510 DARK variant palette, produce the 2610 dark
 * palette — `derive2610Variant` followed by the chat-widget overrides.
 *
 * Overrides only replace values for names the base derivation already emits,
 * so the key set is identical to `derive2610Variant(variant2510)`.
 */
export function derive2610DarkVariant(variant2510Dark) {
  const out = derive2610Variant(variant2510Dark);
  for (const [name, value] of Object.entries(RELEASE_2610_DARK_OVERRIDES)) {
    if (name in out) {
      out[name] = value;
    }
  }
  return out;
}
